import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { sanitizeListingPriceBars } from '../utils/etpChartBars';
import { computeSmaLineData } from '../utils/stockMaChartData';
import {
  applyVisibleRange,
  DEFAULT_ZOOM,
  flattenCuratedEtfs,
  flattenCuratedEtns,
  fmtBucket,
  fmtCostPct,
  fmtPrice,
  fmtReplication,
  fmtSpreadPct,
  syntheticRtc,
  INITIAL_SMA_ACTIVE,
  REPLICATION_TITLES,
  rowKey,
  SMA_DEFS,
  ZOOM_RANGES,
} from '../utils/etpListingHelpers';
import EtcRollPanel from './EtcRollPanel';
import EtnIndexTrackPanel from './EtnIndexTrackPanel';
import './InvestingPage.css';
import './EtpPricesPage.css';

const REPLICATION_FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'physical', label: 'Physical' },
  { id: 'futures', label: 'Futures' },
  { id: 'synthetic', label: 'Synthetic' },
];

const PAGE_VARIANTS = {
  etf: {
    title: 'Curated ETFs',
    subtitle:
      'Amundi, WisdomTree, and strategic basket ETFs — daily OHLCV from Yahoo. ETNs and commodity ETCs have dedicated tabs.',
    flatten: flattenCuratedEtfs,
    loadError: 'Failed to load ETF symbols',
    emptyDb: 'No curated ETF listings in DB — restart investing backend after ETP upsert',
    tableEmpty: 'No ETF listings match this filter.',
    selectPrompt: 'Select an ETF to view price history',
    showRollPanel: true,
  },
  etn: {
    title: 'ETNs',
    subtitle:
      'Crypto ETPs (Europe) and US iPath / ETRACS notes (vol, commodities, carbon, MLP, BDC).',
    flatten: flattenCuratedEtns,
    loadError: 'Failed to load ETN symbols',
    emptyDb: 'No ETN listings in DB — apply Flyway V006, restart investing backend, and upsert basket',
    tableEmpty: 'No ETN listings match this filter.',
    selectPrompt: 'Select an ETN to view price history',
    showRollPanel: true,
    rollPanelUnderTable: true,
  },
};

function ListingPricesPage({ variant = 'etf' }) {
  const page = PAGE_VARIANTS[variant] ?? PAGE_VARIANTS.etf;
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const smaSeriesRef = useRef({});

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replicationFilter, setReplicationFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [bars, setBars] = useState([]);
  const [zoomRange, setZoomRange] = useState(DEFAULT_ZOOM);
  const zoomRangeRef = useRef(DEFAULT_ZOOM);
  const [meta, setMeta] = useState(null);
  const [smaActive, setSmaActive] = useState(INITIAL_SMA_ACTIVE);
  useEffect(() => {
    zoomRangeRef.current = zoomRange;
  }, [zoomRange]);

  useEffect(() => {
    let cancelled = false;

    async function loadEtpUniverse() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/investing/symbols');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        const symbols = page.flatten(data.groups);
        setRows(symbols);
        setMeta({
          prices_fetched_at: data.prices_fetched_at,
          symbol_count: symbols.length,
        });
        if (symbols.length === 0) {
          throw new Error(page.emptyDb);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || page.loadError);
          setRows([]);
          setMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEtpUniverse();
    return () => {
      cancelled = true;
    };
  }, [variant]);

  const bucketTabs = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      if (!row.bucket) return;
      counts.set(row.bucket, (counts.get(row.bucket) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) =>
      fmtBucket(a[0]).localeCompare(fmtBucket(b[0])),
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (replicationFilter !== 'all') {
      next = next.filter((row) => row.replication === replicationFilter);
    }
    if (bucketFilter !== 'all') {
      next = next.filter((row) => row.bucket === bucketFilter);
    }
    return next;
  }, [rows, replicationFilter, bucketFilter]);

  useEffect(() => {
    if (filteredRows.length === 0) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((prev) => {
      if (prev && filteredRows.some((row) => rowKey(row) === rowKey(prev))) {
        return prev;
      }
      return filteredRows[0];
    });
  }, [filteredRows]);

  useEffect(() => {
    if (!selectedRow || !chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        axisPressedMouseMove: true,
        axisDoubleClickReset: true,
        mouseWheel: true,
        pinch: true,
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C',
      downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C',
      wickDownColor: '#C0571F',
    });

    const smaSeries = {};
    SMA_DEFS.forEach((def) => {
      smaSeries[def.key] = chart.addSeries(LineSeries, {
        color: def.color,
        lineWidth: def.lineWidth,
        visible: false,
        priceLineVisible: false,
        lastValueVisible: false,
      });
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    smaSeriesRef.current = smaSeries;

    const disconnectResize = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height });
    });

    return () => {
      disconnectResize();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      smaSeriesRef.current = {};
    };
  }, [selectedRow?.symbol]);

  useEffect(() => {
    if (!bars.length || !candleSeriesRef.current) return;
    candleSeriesRef.current.setData(bars);
    applyVisibleRange(chartRef.current, bars, zoomRangeRef.current);
  }, [bars]);

  useEffect(() => {
    const seriesMap = smaSeriesRef.current;
    if (!bars.length || !Object.keys(seriesMap).length) return;

    SMA_DEFS.forEach((def) => {
      const series = seriesMap[def.key];
      if (!series) return;
      const on = smaActive[def.key];
      if (on) {
        series.setData(computeSmaLineData(bars, def.period));
        series.applyOptions({ visible: true });
      } else {
        series.applyOptions({ visible: false });
      }
    });
  }, [bars, smaActive]);

  const toggleSma = useCallback((key) => {
    setSmaActive((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el || !selectedRow) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectedRow]);

  useEffect(() => {
    if (!selectedRow) {
      setBars([]);
      setChartError(null);
      candleSeriesRef.current?.setData([]);
      return;
    }

    let cancelled = false;

    async function loadChartData() {
      setChartLoading(true);
      setChartError(null);
      try {
        const symbol = encodeURIComponent(selectedRow.symbol);
        const priceResponse = await fetch(`/api/investing/prices/${symbol}`);
        if (!priceResponse.ok) {
          throw new Error(`Prices HTTP ${priceResponse.status}`);
        }
        const priceData = await priceResponse.json();
        if (cancelled) return;

        const anchor = selectedRow.close ?? null;
        const nextBars = sanitizeListingPriceBars(priceData.bars || [], anchor);
        setBars(nextBars);
        candleSeriesRef.current?.setData(nextBars);
        applyVisibleRange(chartRef.current, nextBars, zoomRangeRef.current);
      } catch (err) {
        if (!cancelled) {
          setChartError(err.message || 'Failed to load price history');
          setBars([]);
          candleSeriesRef.current?.setData([]);
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    }

    loadChartData();
    return () => {
      cancelled = true;
    };
  }, [selectedRow]);

  const handleZoomChange = useCallback((rangeLabel) => {
    zoomRangeRef.current = rangeLabel;
    setZoomRange(rangeLabel);
    applyVisibleRange(chartRef.current, bars, rangeLabel);
  }, [bars]);

  const latestBar = bars.length > 0 ? bars[bars.length - 1] : null;
  const displayClose = latestBar?.close ?? selectedRow?.close ?? null;
  const displayDate = latestBar?.time ?? selectedRow?.price_date ?? null;

  return (
    <div className={`etp-prices-page investing-page page-content${variant === 'etn' ? ' etn-prices-page' : ''}`}>
      <header className="etp-prices-header">
        <div>
          <h2 className="etp-prices-title">{page.title}</h2>
          <p className="etp-prices-subtitle">
            {page.subtitle}
            {meta?.prices_fetched_at && (
              <span className="etp-prices-meta">
                {' '}
                Prices updated {meta.prices_fetched_at}
              </span>
            )}
          </p>
        </div>
      </header>

      <div className="investing-left etp-left-column">
        <div className="etp-listings-table-block">
        <div className="investing-controls">
          <span className="investing-count">
            {loading ? 'Loading…' : `${filteredRows.length} listings`}
          </span>
        </div>

        {error && (
          <div className="investing-error">
            Could not load universe: {error}. Is investing_backend running on port 5001?
          </div>
        )}

        <div className="investing-table-wrap">
          <table className="investing-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Issuer</th>
                <th className="investing-th-filter">
                  <span className="investing-th-filter-label">Bucket</span>
                  <select
                    className="investing-th-select"
                    aria-label="Filter by bucket"
                    value={bucketFilter}
                    onChange={(e) => setBucketFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="all">All buckets</option>
                    {bucketTabs.map(([bucket]) => (
                      <option key={bucket} value={bucket}>
                        {fmtBucket(bucket)}
                      </option>
                    ))}
                  </select>
                </th>
                <th className="investing-th-filter">
                  <span className="investing-th-filter-label">Replication</span>
                  <select
                    className="investing-th-select"
                    aria-label="Filter by replication"
                    value={replicationFilter}
                    onChange={(e) => setReplicationFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {REPLICATION_FILTER_OPTIONS.map(({ id, label }) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                </th>
                <th>Last</th>
                <th title="Total expense ratio (justETF OCF)">TER</th>
                <th title="(ask − bid) / mid from Yahoo top of book">Spread</th>
                <th title="Round-trip cost (RTC): TER + 2 × spread">RTC</th>
                <th>As of</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="investing-empty">
                    {page.tableEmpty}
                  </td>
                </tr>
              )}
              {filteredRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={
                    selectedRow && rowKey(selectedRow) === rowKey(row)
                      ? 'investing-row-selected'
                      : ''
                  }
                  onClick={() => {
                    zoomRangeRef.current = DEFAULT_ZOOM;
                    setZoomRange(DEFAULT_ZOOM);
                    setSelectedRow(row);
                  }}
                >
                  <td className="investing-symbol">{row.symbol}</td>
                  <td>{row.name || '—'}</td>
                  <td>{row.issuer || '—'}</td>
                  <td className="etp-bucket-cell" title={row.bucket || ''}>
                    {row.bucket ? (
                      <>
                        {fmtBucket(row.bucket)}
                        {row.hedged && (
                          <span className="etp-hedge-badge" title="EUR hedged share class">
                            H
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {row.replication ? (
                      <span
                        className={`etp-repl-badge etp-repl-${row.replication}`}
                        title={REPLICATION_TITLES[row.replication] || ''}
                      >
                        {fmtReplication(row.replication)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="investing-mono">{fmtPrice(row.close)}</td>
                  <td
                    className="investing-mono"
                    title={row.isin ? `ISIN ${row.isin} (justETF)` : 'Run justETF import for TER'}
                  >
                    {fmtCostPct(row.ter)}
                  </td>
                  <td
                    className="investing-mono"
                    title={
                      row.bid != null && row.ask != null
                        ? `bid ${row.bid} / ask ${row.ask}${
                            row.quote_yahoo_symbol && row.quote_yahoo_symbol !== row.symbol
                              ? ` (via ${row.quote_yahoo_symbol})`
                              : ''
                          }`
                        : row.spread_relative == null
                          ? 'Run ETP quotes import during market hours'
                          : ''
                    }
                  >
                    {fmtSpreadPct(row.spread_relative)}
                  </td>
                  <td
                    className="investing-mono"
                    title={
                      row.ter != null && row.spread_relative != null
                        ? `RTC = ${fmtCostPct(row.ter)} + 2 × ${fmtSpreadPct(row.spread_relative)}`
                        : 'Needs TER and spread'
                    }
                  >
                    {fmtCostPct(row.rtc ?? row.cs ?? syntheticRtc(row.ter, row.spread_relative))}
                  </td>
                  <td className="investing-mono">{row.price_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
        {page.rollPanelUnderTable && selectedRow?.replication === 'futures' && (
          <EtcRollPanel
            className="etc-cot-roll-under-table"
            symbol={selectedRow.symbol}
            replication={selectedRow.replication}
            category={selectedRow.category}
          />
        )}
        {page.rollPanelUnderTable && selectedRow?.benchmark_symbol && (
          <EtnIndexTrackPanel
            className="etc-cot-roll-under-table"
            symbol={selectedRow.symbol}
            benchmarkSymbol={selectedRow.benchmark_symbol}
            benchmarkLabel={selectedRow.benchmark_label}
          />
        )}
      </div>

      <div className="investing-right">
        {!selectedRow && (
          <div className="investing-placeholder">
            <span>{page.selectPrompt}</span>
          </div>
        )}

        {selectedRow && (
          <div className="etp-right-stack">
            <section className="etp-price-dashboard" aria-label="Price and moving averages">
            <div className="investing-chart-header">
              <div className="investing-chart-title">
                <span className="investing-chart-symbol">{selectedRow.symbol}</span>
                <span className="investing-chart-name">{selectedRow.name || '—'}</span>
                {selectedRow.category === 'etn' && (
                  <span className="etp-type-badge etp-type-etn">ETN</span>
                )}
                {selectedRow.replication && (
                  <span
                    className={`etp-repl-badge etp-repl-${selectedRow.replication}`}
                    title={REPLICATION_TITLES[selectedRow.replication] || ''}
                  >
                    {fmtReplication(selectedRow.replication)}
                  </span>
                )}
                {selectedRow.bucket && (
                  <span className="etp-bucket-badge" title={selectedRow.bucket}>
                    {fmtBucket(selectedRow.bucket)}
                    {selectedRow.hedged ? ' · hedged' : ''}
                  </span>
                )}
              </div>
              <dl className="etp-selected-costs" aria-label="Listing costs">
                <div className="etp-selected-cost-item">
                  <dt>ISIN</dt>
                  <dd
                    className="etp-selected-cost-isin"
                    title={selectedRow.isin ? 'Fund ISIN (justETF / listing map)' : 'No ISIN mapped for this ticker'}
                  >
                    {selectedRow.isin || '—'}
                  </dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>TER</dt>
                  <dd
                    title={
                      selectedRow.isin
                        ? `ISIN ${selectedRow.isin} — OCF from justETF`
                        : 'Run justETF import for TER'
                    }
                  >
                    {fmtCostPct(selectedRow.ter)}
                  </dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>Spread</dt>
                  <dd
                    title={
                      selectedRow.bid != null && selectedRow.ask != null
                        ? `bid ${selectedRow.bid} / ask ${selectedRow.ask}${
                            selectedRow.quote_yahoo_symbol &&
                            selectedRow.quote_yahoo_symbol !== selectedRow.symbol
                              ? ` (via ${selectedRow.quote_yahoo_symbol})`
                              : ''
                          }`
                        : selectedRow.spread_relative == null
                          ? 'Run ETP quotes import during market hours'
                          : '(ask − bid) / mid from Yahoo top of book'
                    }
                  >
                    {fmtSpreadPct(selectedRow.spread_relative)}
                  </dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>RTC</dt>
                  <dd
                    title={
                      selectedRow.ter != null && selectedRow.spread_relative != null
                        ? `RTC = ${fmtCostPct(selectedRow.ter)} + 2 × ${fmtSpreadPct(selectedRow.spread_relative)}`
                        : 'Round-trip cost — needs TER and spread'
                    }
                  >
                    {fmtCostPct(
                      selectedRow.rtc ??
                        selectedRow.cs ??
                        syntheticRtc(selectedRow.ter, selectedRow.spread_relative),
                    )}
                  </dd>
                </div>
              </dl>
              <div className="investing-chart-meta">
                {displayClose != null && (
                  <span className="investing-chart-price">
                    {fmtPrice(displayClose)}
                    {displayDate && (
                      <span className="investing-chart-date"> · {displayDate}</span>
                    )}
                  </span>
                )}
                {bars.length > 0 && (
                  <span className="investing-chart-bars">{bars.length} days</span>
                )}
              </div>
              <div className="investing-zoom-toolbar">
                <span className="investing-zoom-label">Range</span>
                {ZOOM_RANGES.map(({ label }) => (
                  <button
                    key={label}
                    type="button"
                    className={`investing-zoom-btn${zoomRange === label ? ' active' : ''}`}
                    onClick={() => handleZoomChange(label)}
                    disabled={chartLoading || bars.length === 0}
                  >
                    {label}
                  </button>
                ))}
                <span className="investing-zoom-hint">Scroll to zoom · drag to pan</span>
              </div>
              <div className="etp-sma-toolbar" aria-label="Simple moving averages">
                <span className="investing-zoom-label">SMA</span>
                {SMA_DEFS.map((def) => (
                  <label
                    key={def.key}
                    className={`etp-sma-toggle${smaActive[def.key] ? ' active' : ''}`}
                    style={{ '--sma-color': def.color }}
                  >
                    <input
                      type="checkbox"
                      checked={!!smaActive[def.key]}
                      onChange={() => toggleSma(def.key)}
                      disabled={chartLoading || bars.length === 0}
                    />
                    <span className="etp-sma-dot" aria-hidden />
                    {def.label}
                  </label>
                ))}
              </div>
            </div>

            {chartError && (
              <div className="investing-error">
                Could not load prices for {selectedRow.symbol}: {chartError}
              </div>
            )}

            {chartLoading && (
              <div className="investing-chart-loading">Loading chart…</div>
            )}

            <div className="investing-charts-area">
              <div
                ref={chartContainerRef}
                className="investing-chart-wrap"
              />
            </div>
            </section>

            {page.showRollPanel && !page.rollPanelUnderTable && (
              <EtcRollPanel
                symbol={selectedRow.symbol}
                replication={selectedRow.replication}
                category={selectedRow.category}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EtpPricesPage() {
  return <ListingPricesPage variant="etf" />;
}

export function EtnPricesPage() {
  return <ListingPricesPage variant="etn" />;
}

export default EtpPricesPage;
